import { useState, useEffect, useCallback } from 'react';
import { bffQuery, bffMutation } from '../lib/bffClient.js';

const LIST_DOCTORS_QUERY = `
  query ListClinicDoctors($clinicId: ID!) {
    listClinicDoctors(clinicId: $clinicId) {
      id clinicId doctorId doctorName active joinedAt
      status registeredVia
      serviceCount visitDurationMinutes siamId bookingRequiresApproval
      presences { dayOfWeek startTime endTime }
    }
  }
`;

const ACCEPT_DOCTOR_MUTATION = `
  mutation AcceptDoctorLink($id: ID!) {
    acceptDoctorLink(id: $id) {
      id status
    }
  }
`;

const UPDATE_DOCTOR_LINK_MUTATION = `
  mutation UpdateDoctorLink($id: ID!, $input: UpdateDoctorLinkInput!) {
    updateDoctorLink(id: $id, input: $input) {
      id visitDurationMinutes siamId bookingRequiresApproval
      presences { dayOfWeek startTime endTime }
    }
  }
`;

const REMOVE_DOCTOR_MUTATION = `
  mutation RemoveDoctorFromClinic($id: ID!) {
    removeDoctorFromClinic(id: $id)
  }
`;

// `keycloakUserId` is the clinic FOUNDER's Keycloak sub. It is the only
// reliable way to identify the owner: the BFF stores the founder as
// `clinic_admin` and never assigns a `clinic_owner` tier, so a tier check alone
// would let the personnel page offer to demote the person who owns the clinic.
// `myClinic` is un-gated (SETUP_OPERATIONS), so asking for it costs no grant.
const MY_CLINIC_QUERY = `query MyClinic { myClinic { id keycloakUserId } }`;

const DOCTOR_INFO_QUERY = `
  query DoctorInfo($doctorId: String!) {
    doctorInfo(doctorId: $doctorId) {
      doctorId name specialty medicalCode bio
      phone nationalId birthDate gender username email source
    }
  }
`;

export function useClinicDoctors() {
  const [clinicId, setClinicId]   = useState(null);
  const [founderSub, setFounder]  = useState('');
  const [doctors,  setDoctors]    = useState([]);
  const [loading,  setLoading]    = useState(true);
  const [error,    setError]      = useState(null);

  const fetchClinic = useCallback(async () => {
    const data = await bffQuery(MY_CLINIC_QUERY, {}, 'MyClinic');
    return data?.myClinic || null;
  }, []);

  // Fetch one doctor's read-only info (doctorpreference profile + Keycloak) by
  // their doctorId. Returns the info object or null; never throws.
  const fetchDoctorInfo = useCallback(async (doctorId) => {
    try {
      const data = await bffQuery(DOCTOR_INFO_QUERY, { doctorId }, 'DoctorInfo');
      return data?.doctorInfo || null;
    } catch {
      return null;
    }
  }, []);

  const load = useCallback(async (cid) => {
    setLoading(true);
    setError(null);
    try {
      const data = await bffQuery(LIST_DOCTORS_QUERY, { clinicId: cid }, 'ListClinicDoctors');
      const list = data?.listClinicDoctors || [];
      setDoctors(list);
      // Auto-enrich each row with the doctor's real info so the name shows by
      // default (in place of the raw id). Runs in the background per row.
      list.forEach(d => {
        if (!d.doctorId) return;
        fetchDoctorInfo(d.doctorId).then(info => {
          if (info) setDoctors(prev => prev.map(x => x.id === d.id ? { ...x, info } : x));
        });
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [fetchDoctorInfo]);

  useEffect(() => {
    fetchClinic().then(clinic => {
      const cid = clinic?.id || null;
      setClinicId(cid);
      setFounder(clinic?.keycloakUserId || '');
      if (cid) load(cid);
      else setLoading(false);
    }).catch(() => setLoading(false));
  }, [fetchClinic, load]);

  const acceptDoctor = useCallback(async (linkId) => {
    await bffMutation(ACCEPT_DOCTOR_MUTATION, { id: linkId }, 'AcceptDoctorLink');
    setDoctors(prev => prev.map(d => d.id === linkId ? { ...d, status: 'accepted' } : d));
  }, []);

  const updateDoctor = useCallback(async (linkId, input) => {
    const data = await bffMutation(UPDATE_DOCTOR_LINK_MUTATION, { id: linkId, input }, 'UpdateDoctorLink');
    const updated = data?.updateDoctorLink;
    if (updated) {
      setDoctors(prev => prev.map(d => d.id === linkId ? { ...d, ...updated } : d));
    }
    return updated;
  }, []);

  const removeDoctor = useCallback(async (linkId) => {
    await bffMutation(REMOVE_DOCTOR_MUTATION, { id: linkId }, 'RemoveDoctorFromClinic');
    setDoctors(prev => prev.filter(d => d.id !== linkId));
  }, []);

  // Read-only: (re)load one doctor's full info by their doctorId and stash it on
  // that row under `info`. Used when opening the details modal. No DB writes.
  const loadDoctorInfo = useCallback(async (linkId, doctorId) => {
    const info = await fetchDoctorInfo(doctorId);
    setDoctors(prev => prev.map(d => d.id === linkId ? { ...d, info } : d));
    return info;
  }, [fetchDoctorInfo]);

  return {
    doctors, loading, error, clinicId, founderSub,
    acceptDoctor, updateDoctor, removeDoctor, loadDoctorInfo,
    reload: () => clinicId && load(clinicId),
  };
}
